import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useLocation, useParams } from "react-router-dom";
import RecordProfilePage from "../../../components/RecordProfilePage";
import {
  fetchEmailTemplateSpecs,
  fetchEmailTemplates,
  previewEmailTemplate,
  saveEmailTemplate,
} from "../../../utils/api";
import {
  extractTemplateVariables,
  formatLanguage,
  formatSpecCode,
  formatVariableLabel,
  getErrorMessage,
  normalizeSpecs,
  normalizeTemplates,
} from "./emailFormatters";
import { BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const sampleValues = {
  patientFirstName: "Maria",
  patientLastName: "Garcia",
  patientFullName: "Maria Garcia",
  scheduledDueDate: "2026-07-21",
  displayFollowUpCode: "ABC123-H1",
  clinicName: "TAMU Biobank",
  recipientEmail: "tester@example.com",
  templateScope: "TEST",
  studyDisplayName: "IPGX and TREC",
};

const PreviewBlock = ({ label, value }) => (
  <Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
      {label}
    </Typography>
    <Box
      sx={{
        p: 2,
        bgcolor: "#fafafa",
        border: "1px solid #e0e0e0",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontSize: "0.875rem",
        lineHeight: 1.6,
      }}
    >
      {value || "N/A"}
    </Box>
  </Box>
);

const EmailTemplateProfile = () => {
  const location = useLocation();
  const { specCode, language } = useParams();
  const [template, setTemplate] = useState(location.state?.template || null);
  const [specs, setSpecs] = useState(location.state?.specs || []);
  const [form, setForm] = useState({
    name: "",
    subjectTemplate: "",
    bodyTextTemplate: "",
    bodyHtmlTemplate: "",
  });
  const [focusedField, setFocusedField] = useState("bodyTextTemplate");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(!location.state?.template);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const htmlRef = useRef(null);

  const loadTemplate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [specResponse, templateResponse] = await Promise.all([
        fetchEmailTemplateSpecs({ specCode }),
        fetchEmailTemplates({ specCode, language }),
      ]);
      const nextSpecs = normalizeSpecs(specResponse);
      const nextTemplate = normalizeTemplates(templateResponse)[0] || null;
      setSpecs(nextSpecs);
      setTemplate(nextTemplate);
      if (!nextTemplate) {
        setError("Template not found.");
      }
    } catch (err) {
      console.error("Failed to load email template:", err);
      setError(getErrorMessage(err, "Failed to load email template."));
    } finally {
      setLoading(false);
    }
  }, [language, specCode]);

  useEffect(() => {
    if (!template) {
      loadTemplate();
    }
  }, [loadTemplate, template]);

  useEffect(() => {
    if (!template) return;
    setForm({
      name: template.name || "",
      subjectTemplate: template.subjectTemplate || "",
      bodyTextTemplate: template.bodyTextTemplate || "",
      bodyHtmlTemplate: template.bodyHtmlTemplate || "",
    });
    setPreview(null);
    setSuccess("");
  }, [template]);

  const spec = useMemo(() => {
    return specs.find((item) => item.specCode === template?.specCode) || template || {};
  }, [specs, template]);

  const requiredVariables = useMemo(() => spec.requiredVariables || template?.requiredVariables || [], [spec, template]);
  const optionalVariables = useMemo(() => spec.optionalVariables || template?.optionalVariables || [], [spec, template]);
  const allowedVariables = useMemo(() => Array.from(new Set([...requiredVariables, ...optionalVariables])), [requiredVariables, optionalVariables]);

  const usedVariables = useMemo(() => Array.from(new Set([
    ...extractTemplateVariables(form.subjectTemplate),
    ...extractTemplateVariables(form.bodyTextTemplate),
    ...extractTemplateVariables(form.bodyHtmlTemplate),
  ])), [form.subjectTemplate, form.bodyTextTemplate, form.bodyHtmlTemplate]);

  const missingRequired = useMemo(() => requiredVariables.filter((variable) => !usedVariables.includes(variable)), [requiredVariables, usedVariables]);
  const unknownVariables = useMemo(() => usedVariables.filter((variable) => !allowedVariables.includes(variable)), [allowedVariables, usedVariables]);

  const canSave = Boolean(
    template?.specCode
      && template?.language
      && form.name.trim()
      && form.subjectTemplate.trim()
      && form.bodyTextTemplate.trim()
      && missingRequired.length === 0
      && unknownVariables.length === 0,
  );

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setPreview(null);
    setSuccess("");
  };

  const insertVariable = (variable) => {
    const field = ["subjectTemplate", "bodyTextTemplate", "bodyHtmlTemplate"].includes(focusedField)
      ? focusedField
      : "bodyTextTemplate";
    const inputRefs = {
      subjectTemplate: subjectRef,
      bodyTextTemplate: bodyRef,
      bodyHtmlTemplate: htmlRef,
    };
    const input = inputRefs[field].current;
    const token = `{{${variable}}}`;
    const current = form[field] || "";
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? current.length;
    const nextValue = `${current.slice(0, start)}${token}${current.slice(end)}`;
    setForm((state) => ({ ...state, [field]: nextValue }));
    setPreview(null);
    setSuccess("");

    window.requestAnimationFrame(() => {
      input?.focus();
      const cursor = start + token.length;
      input?.setSelectionRange(cursor, cursor);
    });
  };

  const handlePreview = async () => {
    if (!canSave) return;
    setPreviewing(true);
    setError("");
    setSuccess("");
    try {
      const response = await previewEmailTemplate({
        specCode: template.specCode,
        language: template.language,
        name: form.name,
        subjectTemplate: form.subjectTemplate,
        bodyTextTemplate: form.bodyTextTemplate,
        bodyHtmlTemplate: form.bodyHtmlTemplate || null,
        sampleVariables: sampleValues,
      });
      setPreview(response?.data || response);
    } catch (err) {
      console.error("Failed to preview email template:", err);
      setError(getErrorMessage(err, "Failed to preview template."));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await saveEmailTemplate(template.specCode, template.language, {
        name: form.name,
        subjectTemplate: form.subjectTemplate,
        bodyTextTemplate: form.bodyTextTemplate,
        bodyHtmlTemplate: form.bodyHtmlTemplate || null,
      });
      setTemplate(response?.data || response);
      setSuccess("Template saved.");
    } catch (err) {
      console.error("Failed to save email template:", err);
      setError(getErrorMessage(err, "Failed to save template."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <RecordProfilePage
      title="Email Template Profile"
      backTo={BIOBANK_SUB_ROUTES.EMAIL_CENTER_TEMPLATES}
      backState={{ refreshTemplates: true }}
    >
      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      )}

      {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && template && (
        <Card sx={{ borderRadius: 2, boxShadow: "none", border: "1px solid #e0e0e0", backgroundColor: "#fff" }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: "#500000", mb: 0.75 }}>
                  {template.name || formatSpecCode(template.specCode)}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={formatSpecCode(template.specCode)} />
                  <Chip size="small" label={formatLanguage(template.language)} />
                  <Chip size="small" label={template.scope || "N/A"} />
                </Stack>
              </Box>

              <Divider />

              <TextField label="Name" size="small" fullWidth value={form.name} onChange={update("name")} />
              <TextField
                label="Subject"
                size="small"
                fullWidth
                value={form.subjectTemplate}
                onChange={update("subjectTemplate")}
                onFocus={() => setFocusedField("subjectTemplate")}
                inputRef={subjectRef}
              />
              <TextField
                label="Body Text"
                fullWidth
                multiline
                minRows={10}
                value={form.bodyTextTemplate}
                onChange={update("bodyTextTemplate")}
                onFocus={() => setFocusedField("bodyTextTemplate")}
                inputRef={bodyRef}
              />
              <TextField
                label="Body HTML"
                fullWidth
                multiline
                minRows={6}
                value={form.bodyHtmlTemplate}
                onChange={update("bodyHtmlTemplate")}
                onFocus={() => setFocusedField("bodyHtmlTemplate")}
                inputRef={htmlRef}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Variables
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {allowedVariables.map((variable) => (
                    <Chip
                      key={variable}
                      size="small"
                      label={formatVariableLabel(variable)}
                      color={requiredVariables.includes(variable) ? "primary" : "default"}
                      onClick={() => insertVariable(variable)}
                    />
                  ))}
                </Stack>
              </Box>

              {unknownVariables.length > 0 && <Alert severity="error">Unknown variables: {unknownVariables.join(", ")}</Alert>}
              {missingRequired.length > 0 && <Alert severity="warning">Missing required variables: {missingRequired.map(formatVariableLabel).join(", ")}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={handlePreview} disabled={!canSave || previewing || saving}>
                  Preview
                </Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={!canSave || saving || previewing}>
                  Save
                </Button>
              </Stack>

              {preview && (
                <>
                  <Divider />
                  <PreviewBlock label="Rendered Subject" value={preview.subject} />
                  <PreviewBlock label="Rendered Body" value={preview.bodyText} />
                  <PreviewBlock label="Rendered HTML" value={preview.bodyHtml} />
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </RecordProfilePage>
  );
};

export default EmailTemplateProfile;
